import { memo, type ComponentProps } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import "./markdown.css"

/* The one markdown renderer for agent prose: legal/financial document typography, no raw HTML,
   no images, no emoji. The mdast transforms below are exported so they can be tested alone. */

/** A minimal mdast node: only the fields the transforms touch. */
export interface MNode {
  type: string
  value?: string
  children?: MNode[]
  data?: { hName?: string; hProperties?: Record<string, unknown> }
  [key: string]: unknown
}

const LEADING_EMOJI = /^[\s\p{Extended_Pictographic}️‍]+/u
const ANY_EMOJI = /(?![\u00A9\u00AE\u2122])[\p{Extended_Pictographic}️‍]/gu
const NUMERIC_CELL = /^[₹$()\-–\d,.\s%]+$/
const TOTAL_ROW = /^\s*(total|grand total|net)/i

export type ChipTone = "ok" | "warn" | "danger"
/** Status marks the agent likes to lead with, said in words. Any other leading emoji is just dropped. */
export function chipFor(lead: string): { word: string; tone: ChipTone } | null {
  if (lead.includes("✅")) return { word: "Ready", tone: "ok" }
  if (lead.includes("✔")) return { word: "Done", tone: "ok" }
  if (lead.includes("⚠")) return { word: "Check", tone: "warn" }
  if (lead.includes("❌")) return { word: "Missing", tone: "danger" }
  return null
}

export function textOf(node: MNode): string {
  if (typeof node.value === "string") return node.value
  return (node.children ?? []).map(textOf).join("")
}

const walk = (node: MNode, fn: (n: MNode) => void) => {
  fn(node)
  node.children?.forEach((c) => walk(c, fn))
}

const firstText = (node: MNode): MNode | null => {
  for (const c of node.children ?? []) {
    if (c.type === "text") return c
    if (c.type === "emphasis" || c.type === "strong") {
      const t = firstText(c)
      if (t) return t
    }
    return null
  }
  return null
}

const EMOJI_HOLDERS = new Set(["heading", "paragraph", "tableCell"])

/** Leading emoji go (a status mark becomes a word chip); emoji anywhere else in the text go too. */
export function remarkNoEmoji() {
  return (tree: MNode) => {
    walk(tree, (node) => {
      if (!EMOJI_HOLDERS.has(node.type) || !node.children?.length) return
      const first = node.children[0]
      if (first.type === "text" && typeof first.value === "string") {
        const m = LEADING_EMOJI.exec(first.value)
        if (m && /\p{Extended_Pictographic}/u.test(m[0])) {
          first.value = first.value.slice(m[0].length)
          const chip = chipFor(m[0])
          if (chip) {
            node.children.unshift({ type: "chip", data: { hName: "span", hProperties: { className: ["chip", chip.tone] } }, children: [{ type: "text", value: chip.word }] })
          }
        }
      }
    })
    walk(tree, (node) => {
      if (node.type === "text" && typeof node.value === "string") node.value = node.value.replace(ANY_EMOJI, "")
    })
  }
}

interface KvLine { label: string; value: MNode[] }

/** "**Label:** value" lines of one paragraph, or null when any line is not of that form. */
function kvLines(p: MNode): KvLine[] | null {
  if (p.type !== "paragraph" || !p.children?.length) return null
  const lines: MNode[][] = [[]]
  for (const c of p.children) {
    if (c.type === "break") { lines.push([]); continue }
    if (c.type === "text" && typeof c.value === "string" && c.value.includes("\n")) {
      c.value.split("\n").forEach((part, i) => {
        if (i > 0) lines.push([])
        if (part) lines[lines.length - 1].push({ type: "text", value: part })
      })
      continue
    }
    lines[lines.length - 1].push(c)
  }
  const out: KvLine[] = []
  for (const line of lines) {
    if (!line.length) continue
    const [head, ...rest] = line
    if (head.type !== "strong") return null
    let label = textOf(head).trim()
    let value = rest
    if (label.endsWith(":")) label = label.slice(0, -1).trim()
    else if (rest[0]?.type === "text" && /^\s*:/.test(rest[0].value ?? "")) value = [{ ...rest[0], value: (rest[0].value ?? "").replace(/^\s*:/, "") }, ...rest.slice(1)]
    else return null
    if (!label) return null
    const trimmed = value.map((n) => ({ ...n }))
    if (trimmed[0]?.type === "text") trimmed[0].value = (trimmed[0].value ?? "").replace(/^\s+/, "")
    const last = trimmed[trimmed.length - 1]
    if (last?.type === "text") last.value = (last.value ?? "").replace(/\s+$/, "")
    out.push({ label, value: trimmed.filter((n) => n.type !== "text" || n.value) })
  }
  return out.length ? out : null
}

const dlOf = (entries: KvLine[]): MNode => ({
  type: "kvlist",
  data: { hName: "dl", hProperties: { className: ["kv"] } },
  children: entries.flatMap((e): MNode[] => [
    { type: "kvterm", data: { hName: "dt" }, children: [{ type: "text", value: e.label }] },
    { type: "kvdef", data: { hName: "dd" }, children: e.value },
  ]),
})

/** Two or more "**Label:** value" paragraphs in a row, or a list of them, become one definition grid. */
export function remarkKeyValue() {
  return (tree: MNode) => {
    walk(tree, (parent) => {
      const kids = parent.children
      if (!kids || !["root", "blockquote", "listItem"].includes(parent.type)) return
      const next: MNode[] = []
      let run: KvLine[] = []
      let held: MNode[] = []
      const flush = () => {
        if (run.length >= 2) next.push(dlOf(run))
        else next.push(...held)
        run = []
        held = []
      }
      for (const kid of kids) {
        const lines = kvLines(kid)
        if (lines) { run.push(...lines); held.push(kid); continue }
        if (kid.type === "list" && !kid.ordered && kid.children && kid.children.length >= 2) {
          const items = kid.children.map((li) => (li.children?.length === 1 ? kvLines(li.children[0]) : null))
          if (items.every((x): x is KvLine[] => !!x)) { flush(); next.push(dlOf(items.flat())); continue }
        }
        flush()
        next.push(kid)
      }
      flush()
      parent.children = next
    })
  }
}

const cls = (node: MNode, name: string) => {
  node.data = { ...node.data, hProperties: { ...node.data?.hProperties, className: [...((node.data?.hProperties?.className as string[]) ?? []), name] } }
}

/** Numeric columns are right-aligned, and a Total / Grand Total / Net row gets its closing rule. */
export function remarkTableMarks() {
  return (tree: MNode) => {
    walk(tree, (table) => {
      if (table.type !== "table" || !table.children?.length) return
      const [, ...body] = table.children
      const width = Math.max(...table.children.map((r) => r.children?.length ?? 0))
      for (let col = 0; col < width; col++) {
        const cells = body.map((r) => r.children?.[col]).filter((c): c is MNode => !!c).map((c) => textOf(c).trim()).filter(Boolean)
        const numeric = cells.length > 0 && cells.every((t) => NUMERIC_CELL.test(t) && /\d/.test(t))
        if (numeric) table.children.forEach((r) => { if (r.children?.[col]) cls(r.children[col], "num") })
      }
      body.forEach((r) => { if (r.children?.[0] && TOTAL_ROW.test(textOf(r.children[0]))) cls(r, "total") })
    })
  }
}

const isChip = (n?: MNode) => n?.type === "chip"
const leadChip = (li: MNode) => {
  const p = li.children?.[0]
  return p?.type === "paragraph" && isChip(p.children?.[0]) ? p : null
}

/** A list whose items all lead with a status chip loses its bullets (the chips line up). Under a
    "conflict" heading the chips go and each item is a statement with a muted detail line. */
export function remarkStatusLists() {
  return (tree: MNode) => {
    walk(tree, (parent) => {
      let conflictHeading = false
      for (const kid of parent.children ?? []) {
        if (kid.type === "heading") conflictHeading = /conflict/i.test(textOf(kid))
        if (kid.type !== "list" || !kid.children?.length) continue
        if (conflictHeading) {
          cls(kid, "conflicts")
          for (const li of kid.children) {
            li.spread = true
            const p = li.children?.[0]
            if (!p || p.type !== "paragraph" || !p.children) continue
            if (isChip(p.children[0])) p.children.shift()
            if (p.children[0]?.type === "text") p.children[0].value = (p.children[0].value ?? "").replace(/^\s+/, "")
            const at = p.children.findIndex((c) => c.type === "break" || (c.type === "text" && (c.value ?? "").includes("\n")))
            if (at < 0) continue
            const c = p.children[at]
            const after: MNode[] = p.children.slice(at + 1)
            if (c.type === "text") {
              const [head, ...tail] = (c.value ?? "").split("\n")
              c.value = head
              if (tail.join("\n").trim()) after.unshift({ type: "text", value: tail.join(" ").trim() })
            }
            p.children = p.children.slice(0, at + 1)
            if (after.length) li.children!.splice(1, 0, { type: "paragraph", children: after, data: { hProperties: { className: ["detail"] } } })
          }
        } else if (kid.children.every((li) => leadChip(li))) {
          cls(kid, "chips")
        }
      }
    })
  }
}

const PLUGINS = [remarkGfm, remarkNoEmoji, remarkKeyValue, remarkStatusLists, remarkTableMarks] as const

type Anchor = ComponentProps<"a"> & { node?: unknown }
const components = {
  a: ({ node: _n, ...props }: Anchor) => <a {...props} target="_blank" rel="noopener noreferrer" />,
  table: ({ node: _n, ...props }: ComponentProps<"table"> & { node?: unknown }) => <div className="md-scroll"><table {...props} /></div>,
}

/** Agent prose as a professional document: sans body, serif titles, hairline tables, key/value grids. */
export const Markdown = memo(function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={className ? `md ${className}` : "md"}>
      <ReactMarkdown remarkPlugins={[...PLUGINS]} skipHtml disallowedElements={["img"]} components={components}>{children}</ReactMarkdown>
    </div>
  )
})
